/**
 * Utility functions for system admin dashboard calculations and data transformation
 */

export interface TaskCounts {
  pending: number;
  inProgress: number;
  completed: number;
  blocked: number;
}

export interface StageInfo {
  stage: string;
  stageName: string;
  displayName: string;
  color: string;
  icon: string;
  description: string;
}

/**
 * Calculate percentage of completed tasks
 */
export function calculatePercentage(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Get total count of tasks across all statuses
 */
export function getTotalTaskCount(counts: TaskCounts): number {
  return counts.pending + counts.inProgress + counts.completed + counts.blocked;
}

/**
 * Determine current stage based on organization status
 */
export function getCurrentStage(organizationStatus: string): StageInfo {
  const stageMap: Record<string, StageInfo> = {
    PENDING_REVIEW: {
      stage: 'PENDING_REVIEW',
      stageName: 'pending_review',
      displayName: 'Pending Review',
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      icon: 'clock',
      description: 'Awaiting platform team review',
    },
    APPROVED: {
      stage: 'APPROVED',
      stageName: 'approved',
      displayName: 'Approved',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      icon: 'check-circle',
      description: 'Approved by platform team',
    },
    AWAITING_PAYMENT: {
      stage: 'AWAITING_PAYMENT',
      stageName: 'awaiting_payment',
      displayName: 'Awaiting Payment',
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      icon: 'alert-circle',
      description: 'Payment link sent, awaiting payment',
    },
    PAYMENT_RECEIVED: {
      stage: 'PAYMENT_RECEIVED',
      stageName: 'payment_received',
      displayName: 'Payment Received',
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      icon: 'check-circle',
      description: 'Payment received, setup in progress',
    },
    IT_IN_PROGRESS: {
      stage: 'IT_IN_PROGRESS',
      stageName: 'it_in_progress',
      displayName: 'IT Setup In Progress',
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      icon: 'settings',
      description: 'IT provisioning in progress',
    },
    DATA_CONFIGURATION: {
      stage: 'DATA_CONFIGURATION',
      stageName: 'data_configuration',
      displayName: 'Data Configuration',
      color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      icon: 'database',
      description: 'Data pipeline configuration in progress',
    },
    FINAL_REVIEW: {
      stage: 'FINAL_REVIEW',
      stageName: 'final_review',
      displayName: 'Final Review',
      color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      icon: 'alert-circle',
      description: 'Ready for final review and activation',
    },
    ACTIVATED: {
      stage: 'ACTIVATED',
      stageName: 'activated',
      displayName: 'Activated',
      color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      icon: 'check-circle',
      description: 'Organization is live and active',
    },
    REJECTED: {
      stage: 'REJECTED',
      stageName: 'rejected',
      displayName: 'Rejected',
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      icon: 'x-circle',
      description: 'Application has been rejected',
    },
    DEACTIVATION_PENDING: {
      stage: 'DEACTIVATION_PENDING',
      stageName: 'deactivation_pending',
      displayName: 'Deactivation Pending',
      color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      icon: 'alert-circle',
      description: 'Awaiting deactivation approval',
    },
    DEACTIVATED: {
      stage: 'DEACTIVATED',
      stageName: 'deactivated',
      displayName: 'Deactivated',
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      icon: 'x-circle',
      description: 'Organization has been deactivated',
    },
  };

  return (
    stageMap[organizationStatus] || {
      stage: organizationStatus,
      stageName: organizationStatus.toLowerCase(),
      displayName: organizationStatus.replace(/_/g, ' '),
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      icon: 'circle',
      description: 'Unknown status',
    }
  );
}

/**
 * Format task status for display
 */
export function formatTaskStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get color classes based on status
 */
export function getStatusColor(
  status: string
): {
  bg: string;
  text: string;
  badge: string;
} {
  const statusMap: Record<
    string,
    { bg: string; text: string; badge: string }
  > = {
    PENDING: {
      bg: 'bg-yellow-50 dark:bg-yellow-950',
      text: 'text-yellow-700 dark:text-yellow-200',
      badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
    IN_PROGRESS: {
      bg: 'bg-blue-50 dark:bg-blue-950',
      text: 'text-blue-700 dark:text-blue-200',
      badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
    COMPLETED: {
      bg: 'bg-green-50 dark:bg-green-950',
      text: 'text-green-700 dark:text-green-200',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    BLOCKED: {
      bg: 'bg-red-50 dark:bg-red-950',
      text: 'text-red-700 dark:text-red-200',
      badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
  };

  return (
    statusMap[status] || {
      bg: 'bg-gray-50 dark:bg-gray-900',
      text: 'text-gray-700 dark:text-gray-200',
      badge: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    }
  );
}

/**
 * Get icon name based on status
 */
export function getStatusIcon(status: string): string {
  const iconMap: Record<string, string> = {
    PENDING: 'clock',
    IN_PROGRESS: 'loader',
    COMPLETED: 'check-circle',
    BLOCKED: 'alert-triangle',
  };

  return iconMap[status] || 'circle';
}

/**
 * Format date for display
 */
export function formatDate(date: string | null | undefined): string {
  if (!date) return '-';

  try {
    const dateObj = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return dateObj.toLocaleDateString();
  } catch {
    return date;
  }
}

/**
 * Calculate progress percentage for an organization
 */
export function calculateOrgProgress(
  itPending: number,
  itCompleted: number,
  dataPending: number,
  dataCompleted: number,
  platformPending: number,
  platformCompleted: number
): number {
  const totalTasks =
    itPending + itCompleted + dataPending + dataCompleted + platformPending + platformCompleted;

  if (totalTasks === 0) return 0;

  const completedTasks = itCompleted + dataCompleted + platformCompleted;
  return Math.round((completedTasks / totalTasks) * 100);
}

/**
 * Get progress bar color based on percentage
 */
export function getProgressBarColor(percentage: number): string {
  if (percentage === 0) return 'from-gray-300 to-gray-400';
  if (percentage < 25) return 'from-red-400 to-red-500';
  if (percentage < 50) return 'from-orange-400 to-orange-500';
  if (percentage < 75) return 'from-yellow-400 to-yellow-500';
  if (percentage < 100) return 'from-blue-400 to-blue-500';
  return 'from-green-400 to-green-500';
}

/**
 * Determine if a task is overdue (optional enhancement)
 */
export function isOverdue(createdAt: string, hours: number = 72): boolean {
  const created = new Date(createdAt);
  const now = new Date();
  const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
  return diffHours > hours;
}
